// Niveau 3 — Resten af top (u w o q p å)

import type { CurriculumTier, Trin } from '../../types';
import { NIVEAU_1_SPRINT_WORDS } from './niveau-1';
import { NIVEAU_2_SPRINT_WORDS, NIVEAU_2_UNLOCKED_KEYS } from './niveau-2';
import { filterWords, fromChunks, interleaved, round } from './generators';
import { consolidationTrin, pairTrin, singleKeyTrin } from './trinBuilders';

let prior = [...NIVEAU_2_UNLOCKED_KEYS];

const t1 = singleKeyTrin(
  'niveau-3',
  1,
  'U',
  'U med højre pegefinger.',
  'Ram U med højre pegefinger — makker til Y.',
  'u',
  prior,
  {
    intro: 'u u ju fu ku u u ju fu ku',
    connect: 'ju fu ku yu ru tu ju fu ku yu',
    recall1: 'ju fu ku yu ru tu ju fu ku',
    recall2: 'uj fu ku yu ru tu uj fu ku',
    challenge: 'jufu kuyu rutu juku',
  },
  { palette: 'echo' },
);
prior = t1.unlockedKeys;

const t2 = pairTrin(
  'niveau-3',
  2,
  'W og O',
  'W og O med ring- og lillefinger.',
  'Ram W og O uden at flytte håndfladen.',
  ['w', 'o'],
  prior,
  {
    intro: 'w o w o wo ow w o w o wo ow',
    connect: 'sw lo fw ow sw lo fw ow sw lo',
    recall1: 'wo sw lo fw ow wo sw lo fw',
    recall2: 'ow sw lo fw ow ow sw lo fw',
    challenge: 'swlo fwow wosw lofw owsw',
  },
  { palette: 'echo' },
);
prior = t2.unlockedKeys;

const t3 = pairTrin(
  'niveau-3',
  3,
  'Q og P',
  'Yderste taster på øverste række.',
  'Ram Q og P med lillefingrene.',
  ['q', 'p'],
  prior,
  {
    intro: 'q p q p qp pq q p q p qp pq',
    connect: 'sq lp fq pq sq lp fq pq sq lp',
    recall1: 'qp sq lp fq pq qp sq lp fq',
    recall2: 'pq sq lp fq pq pq sq lp fq',
    challenge: 'sqlp fqpq pqsq lpfq',
  },
  { palette: 'echo' },
);
prior = t3.unlockedKeys;

const t4 = singleKeyTrin(
  'niveau-3',
  4,
  'Å',
  'Det sidste danske vokal.',
  'Ram Å med lillefingeren.',
  'å',
  prior,
  {
    intro: 'å å lå ål å å lå ål å å',
    connect: 'så då få jå kå lå hå gå',
    recall1: 'så då få jå kå lå hå gå',
    recall2: 'få så då jå kå lå hå gå',
    challenge: 'så då få jå kå lå hå gå ål',
  },
  { palette: 'echo' },
);
prior = t4.unlockedKeys;

const NIVEAU_3_WORD_POOL = [
  'du', 'vi', 'op', 'på', 'for', 'ud', 'nu', 'to',
  'tro', 'sko', 'spil', 'plads', 'løb', 'båd', 'så', 'må',
  'gå', 'stå', 'hoved', 'ord', 'hop', 'kop', 'top', 'og',
  'som', 'god', 'bog', 'bon', 'mor', 'niveau', 'web', 'wok',
  'sol', 'sort', 'drop', 'prop', 'hus', 'huske', 'under', 'uden',
  'over', 'holde', 'vores', 'går', 'står', 'måske', 'wiki', 'kor',
  'pose', 'post', 'port', 'park', 'torv', 'noget', 'sov',
  'roser', 'år', 'øl', 'kone', 'dør', 'tog', 'plan', 'krop',
  'krog', 'klog', 'pop', 'moder', 'morgen', 'vindue', 'punkt', 'vogn',
  'hoppe', 'stop', 'stol', 'spot', 'sport', 'plade', 'pluk', 'plukke',
  'hovedet',
  // Navne (dictation-names.json)
  'åge', 'åse', 'bo', 'bodil', 'ella', 'frode', 'gunhild', 'holger', 'jesper',
  'knud', 'kåre', 'lotte', 'lone', 'mogens', 'ole', 'olivia',
  'pia', 'poul', 'preben', 'rasmus', 'sofie', 'torben', 'tove', 'ulla', 'viggo',
];

export const NIVEAU_3_SPRINT_WORDS = filterWords(NIVEAU_3_WORD_POOL, prior);

const t5Base = consolidationTrin(
  'niveau-3',
  5,
  'Konsolidering',
  'Bland U, W, O, Q, P og Å.',
  'Skriv flydende med hele øverste rækken.',
  prior,
  NIVEAU_3_SPRINT_WORDS,
  'en god bog er ny',
);

const t5: Trin = {
  ...t5Base,
  rounds: [
    ...t5Base.rounds,
    round({
      id: 'niveau-3-trin-5-r7',
      title: 'Bland med Niveau 1+2',
      purpose: 'Ord på tværs af alle hidtil lærte taster.',
      mode: 'recall',
      generate: interleaved(
        [
          { chunks: NIVEAU_1_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_2_SPRINT_WORDS, weight: 1 },
          { chunks: NIVEAU_3_SPRINT_WORDS, weight: 2 },
        ],
        90,
      ),
    }),
    round({
      id: 'niveau-3-trin-5-r8',
      title: 'Flash-genkald',
      purpose: 'Se ordet kort, skriv det fra hukommelse.',
      mode: 'recall',
      generate: fromChunks(NIVEAU_3_SPRINT_WORDS, 50),
      presentation: { kind: 'echo', flashMs: 1500, unit: 'word' },
    }),
  ],
};

export const NIVEAU_3_UNLOCKED_KEYS = prior;

export const NIVEAU_3_TRINS: Trin[] = [t1, t2, t3, t4, t5];

export const NIVEAU_3_TIER: CurriculumTier = {
  id: 'niveau-3',
  title: 'Niveau 3',
  description: 'Resten af top: U, W, O, Q, P og Å.',
  trins: NIVEAU_3_TRINS,
};
